import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddToFavoriteActionButton from '../../../../components/designe/ActionButtons/AddToFavoriteActionButton';
import CancelPublishActionButton from '../../../../components/designe/ActionButtons/CancelPublishActionButton';
import CopyActionButton from '../../../../components/designe/ActionButtons/CopyActionButton';
import DeleteActionButton from '../../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../../components/designe/ActionButtons/EditActionButton';
import PublishActionButton from '../../../../components/designe/ActionButtons/PublishActionButton';
import RemoveFavoriteActionButton from '../../../../components/designe/ActionButtons/RemoveFromFavoriteActionButton';
import AppExerciseAvatar from '../../../../components/designe/app-entity-avatar/app-exercise-avatar/AppExerciseAvatar';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useGetAppFeatures } from '../../../../redux/api/appConfigApi';
import {
  AppFeatureKey,
  AvatarSize,
  ExerciseScope,
} from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';
import { IExercise } from '../../../../types/interfaces/exercise';
import { useStyles } from './ExerciseItem.styles';

interface IExerciseItem {
  exercise: IExercise;
  onDeleteClick: (id: string) => void;
  onCopyClick: (id: string) => void;
  onEditClick: (id: string) => void;
  onFollowClick: (id: string) => void;
  onPublishClick: (exercise: IExercise) => void;
  onCancelPublishClick: (exercise: IExercise) => void;
}

const ExerciseItem = (props: IExerciseItem) => {
  const {
    exercise,
    onCopyClick,
    onDeleteClick,
    onEditClick,
    onFollowClick,
    onPublishClick,
    onCancelPublishClick,
  } = props;
  const { t } = useTranslation();
  const { navigate } = useNavigationStack();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { data: features } = useGetAppFeatures();
  const isSharingExercisesActive = features?.find(
    (feature) => feature.key === AppFeatureKey.sharingExercises,
  )?.isActive;

  return (
    <div
      className={classes.exerciseItemWrapper}
      onClick={() => navigate(Paths.exercises.toSummaryId(exercise.id))}>
      <Box className={classes.exerciseItemInfoWrapper}>
        <Box display="flex" gap={1} width="100%">
          <AppExerciseAvatar exercise={exercise} size={AvatarSize.m} />
          <Box
            display="flex"
            flexDirection="column"
            gap={0.5}
            flex={1}
            className={classes.exerciseItemContent}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={0.5}>
              <Typography variant="h3">{exercise.title}</Typography>
              <Box
                onClick={(e) => e.stopPropagation()}
                display="flex"
                flexShrink={0}
                gap={0.5}>
                {isSharingExercisesActive &&
                  exercise?.permissions?.canFollow && (
                    <AddToFavoriteActionButton
                      onClick={() => onFollowClick(exercise.id)}
                    />
                  )}
                {isSharingExercisesActive &&
                  exercise?.permissions?.canUnfollow && (
                    <RemoveFavoriteActionButton
                      onClick={() => onFollowClick(exercise.id)}
                    />
                  )}
                {isSharingExercisesActive &&
                  exercise?.permissions?.canPublish && (
                    <PublishActionButton
                      onClick={() => onPublishClick(exercise)}
                    />
                  )}
                {isSharingExercisesActive &&
                  exercise?.permissions?.canCancelPublish && (
                    <CancelPublishActionButton
                      onClick={() => onCancelPublishClick(exercise)}
                    />
                  )}
                {exercise?.permissions?.canCopy && (
                  <CopyActionButton onClick={() => onCopyClick(exercise.id)} />
                )}
                {exercise?.permissions?.canEdit && (
                  <EditActionButton onClick={() => onEditClick(exercise.id)} />
                )}
                {exercise?.permissions?.canDelete && (
                  <DeleteActionButton
                    onClick={() => onDeleteClick(exercise.id)}
                  />
                )}
              </Box>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={0.5} marginTop={1}>
              <Typography variant={'success-wrapped-sm'}>
                {exercise.category.name}
              </Typography>
              <Typography variant={'success-wrapped-sm'}>
                {exercise.mainMuscleGroup.name}
              </Typography>
              {exercise.additionalMuscles?.map((muscle) => (
                <Typography variant={'grey-wrapped-sm'} key={muscle.id}>
                  {muscle.name}
                </Typography>
              ))}
              <Typography variant={'success-wrapped-sm'}>
                {exercise.equipment.name}
              </Typography>
              {exercise.additionalEquipments?.map((equipment) => (
                <Typography variant={'grey-wrapped-sm'} key={equipment.id}>
                  {equipment.name}
                </Typography>
              ))}
              {exercise.developedSkills?.map((skill) => (
                <Typography key={skill.id} variant="primary-wrapped-sm">
                  {skill.name}
                </Typography>
              ))}
              {/* todo add permissions check */}
              {isSharingExercisesActive && false && (
                <>
                  {exercise.scope === ExerciseScope.publicRequest && (
                    <Typography variant={'success-wrapped-sm'}>
                      {t('exercises.publishRequestedMessage')}
                    </Typography>
                  )}
                  {exercise.scope === ExerciseScope.publicReject && (
                    <Typography variant={'error-wrapped-sm'}>
                      {t('exercises.publishRejectMessage')}
                    </Typography>
                  )}
                  {exercise.scope === ExerciseScope.public && (
                    <Typography variant={'success-wrapped-sm'}>
                      {t('exercises.publishedMessage')}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default ExerciseItem;
